import type { DomainEventDefinition, JsonValue, RecordDefinition, Transaction } from "@e308/core";
import { cloneCombatState } from "./combat-state.js";
import type { SeatModel } from "./content-types.js";
import { participantAudience, shipmentPayload } from "./diplomacy-commands.js";
import { publicIncident, publicPair } from "./diplomacy-events.js";
import {
  cloneDiplomacy,
  INCIDENT_INTERVAL_MS,
  INCIDENT_WARNING_MS,
  PACT_NOTICE_MS,
  pairKey,
} from "./diplomacy-state.js";
import type {
  DiplomacyState,
  IncidentAnnouncement,
  IncidentId,
  MatchLogPayload,
  PactAction,
  PactState,
} from "./diplomacy-types.js";
import { buildingRule } from "./economy-rules.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { borderIsActive } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { effectMultiplier, effectsForResearch } from "./research-effects.js";

type LogEvent = DomainEventDefinition<MatchLogPayload & JsonValue>;

export function resolvePactStep(
  transaction: Transaction<Micro>,
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  logEvent: LogEvent,
): void {
  const nowMs = transaction.gameTimeMs();
  let state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  const expired = state.pacts.filter(
    (pact) => pact.status === "notice" && (pact.noticeEndsAtMs as number) <= nowMs,
  );
  let pacts = state.pacts
    .filter((pact) => !expired.includes(pact))
    .map((pact) => Object.freeze({ ...pact }));
  for (const pact of expired)
    transaction.emit(logEvent, publicPair("pact-ended", pact.left, pact.right));
  const grouped = new Map<string, PactAction[]>();
  for (const action of state.pendingActions) {
    const key = pairKey(action.actorId, action.targetId);
    grouped.set(key, [...(grouped.get(key) ?? []), action]);
  }
  for (const key of [...grouped.keys()].sort()) {
    const actions = grouped.get(key) as PactAction[];
    const [left, right] = key.split(":") as [string, string];
    const current = pacts.find((pact) => pairKey(pact.left, pact.right) === key);
    const next = resolvePair(transaction, current, actions, left, right, nowMs, logEvent);
    pacts = pacts.filter((pact) => pairKey(pact.left, pact.right) !== key);
    if (next) pacts.push(next);
  }
  state = cloneDiplomacy({
    ...state,
    pacts: pacts.sort((left, right) =>
      pairKey(left.left, left.right).localeCompare(pairKey(right.left, right.right)),
    ),
    pendingActions: [],
  });
  transaction.setRecord(diplomacyDefinition, state as DiplomacyState & JsonValue);
}

function resolvePair(
  transaction: Transaction<Micro>,
  current: PactState | undefined,
  actions: readonly PactAction[],
  left: string,
  right: string,
  nowMs: number,
  logEvent: LogEvent,
): PactState | undefined {
  const ordered = [...actions].sort((a, b) => a.actorId.localeCompare(b.actorId));
  if (current?.status === "notice") return current;
  const ending = ordered.find((action) => action.kind === "end");
  if (current?.status === "active") {
    if (!ending) return current;
    transaction.emit(logEvent, {
      ...publicPair("pact-notice-issued", ending.actorId, ending.targetId),
      expiresAtMs: nowMs + PACT_NOTICE_MS,
    });
    return pact(left, right, "notice", [], nowMs + PACT_NOTICE_MS);
  }
  let proposals = [...(current?.proposals ?? [])];
  const latestProposal = new Map<string, PactAction>();
  for (const action of ordered.filter((entry) => entry.kind === "propose"))
    latestProposal.set(action.actorId, action);
  for (const action of [...latestProposal.values()].sort((a, b) =>
    a.actorId.localeCompare(b.actorId),
  )) {
    proposals = [
      ...proposals.filter((entry) => entry.from !== action.actorId),
      { from: action.actorId, message: action.message },
    ].sort((a, b) => a.from.localeCompare(b.from));
    transaction.emit(
      logEvent,
      {
        kind: "pact-proposed",
        actorId: action.actorId,
        targetId: action.targetId,
        message: action.message,
      },
      participantAudience(left, right),
    );
  }
  const accepted = ordered
    .filter((entry) => entry.kind === "accept")
    .find((action) => proposals.some((proposal) => proposal.from === action.targetId));
  if (accepted) {
    transaction.emit(logEvent, publicPair("pact-accepted", accepted.actorId, accepted.targetId));
    return pact(left, right, "active", [], null);
  }
  return proposals.length > 0 ? pact(left, right, "open", proposals, null) : undefined;
}

export function resolveShipmentStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  logEvent: LogEvent,
): void {
  const byId = new Map(seats.map((seat) => [seat.seat.id, seat]));
  const state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  let changed = false;
  const shipments = state.shipments.map((shipment) => {
    if (shipment.status !== "in-transit" || transaction.gameTimeMs() < shipment.deliversAtMs)
      return shipment;
    changed = true;
    const sender = byId.get(shipment.senderId) as SeatModel;
    const recipient = byId.get(shipment.recipientId) as SeatModel;
    const delivered =
      transaction.get(sender.resources.headquarters) > 0n &&
      transaction.get(recipient.resources.headquarters) > 0n;
    if (delivered) transaction.add(recipient.resources.metal, microFromString(shipment.amount));
    const next = Object.freeze({
      ...shipment,
      status: delivered ? ("delivered" as const) : ("lost" as const),
    });
    transaction.emit(
      logEvent,
      shipmentPayload(delivered ? "metal-delivered" : "metal-lost", next),
      participantAudience(next.senderId, next.recipientId),
    );
    return next;
  });
  if (changed)
    transaction.setRecord(
      diplomacyDefinition,
      cloneDiplomacy({ ...state, shipments }) as DiplomacyState & JsonValue,
    );
}

export function cleanupDiplomacyStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  logEvent: LogEvent,
): void {
  const dead = new Set(
    seats
      .filter((seat) => transaction.get(seat.resources.headquarters) <= 0n)
      .map((seat) => seat.seat.id),
  );
  const state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  const ended = transaction.getRecord(lifecycleDefinition).status === "ended";
  if (dead.size === 0 && !ended) return;
  const removedPacts = state.pacts.filter((pact) => dead.has(pact.left) || dead.has(pact.right));
  const shipments = state.shipments.map((shipment) => {
    if (
      shipment.status !== "in-transit" ||
      (!dead.has(shipment.senderId) && !dead.has(shipment.recipientId))
    )
      return shipment;
    const lost = Object.freeze({ ...shipment, status: "lost" as const });
    transaction.emit(
      logEvent,
      shipmentPayload("metal-lost", lost),
      participantAudience(lost.senderId, lost.recipientId),
    );
    return lost;
  });
  for (const pact of removedPacts)
    transaction.emit(logEvent, publicPair("pact-ended", pact.left, pact.right));
  if (ended && state.announcedIncident)
    transaction.emit(logEvent, publicIncident("incident-cancelled", state.announcedIncident));
  transaction.setRecord(
    diplomacyDefinition,
    cloneDiplomacy({
      ...state,
      pacts: state.pacts.filter((pact) => !removedPacts.includes(pact)),
      pendingActions: state.pendingActions.filter(
        (action) => !dead.has(action.actorId) && !dead.has(action.targetId),
      ),
      shipments,
      announcedIncident: ended ? null : state.announcedIncident,
    }) as DiplomacyState & JsonValue,
  );
}

export function resolveIncidentStep(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycleDefinition: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  shieldCapacities: ReadonlyMap<string, Micro>,
  logEvent: LogEvent,
): void {
  let state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  if (state.eventSetting === "off") return;
  const lifecycle = transaction.getRecord(lifecycleDefinition) as MatchLifecycleState;
  const nowMs = transaction.gameTimeMs();
  if (
    lifecycle.status === "active" &&
    !state.announcedIncident &&
    state.nextIncidentAtMs < lifecycle.timeLimitMs &&
    nowMs >= state.nextIncidentAtMs - INCIDENT_WARNING_MS
  ) {
    const announcement = chooseIncident(transaction, seats, state.nextIncidentAtMs);
    state = cloneDiplomacy({ ...state, announcedIncident: announcement });
    transaction.emit(logEvent, publicIncident("incident-warning", announcement));
  }
  if (state.announcedIncident && nowMs >= state.announcedIncident.resolvesAtMs) {
    const applied =
      lifecycle.status === "active" &&
      applyIncident(transaction, seats, lifecycle, state.announcedIncident, shieldCapacities);
    transaction.emit(
      logEvent,
      publicIncident(applied ? "incident-resolved" : "incident-cancelled", state.announcedIncident),
    );
    state = cloneDiplomacy({
      ...state,
      nextIncidentAtMs: state.nextIncidentAtMs + INCIDENT_INTERVAL_MS,
      announcedIncident: null,
    });
  }
  transaction.setRecord(diplomacyDefinition, state as DiplomacyState & JsonValue);
}

function chooseIncident(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  resolvesAtMs: number,
): IncidentAnnouncement {
  const random = transaction.random(["world-events", "incidents"]);
  const ids: readonly IncidentId[] = ["meteor-warning", "valley-salvage-train", "clear-skies"];
  const id = ids[random.bounded(ids.length)] as IncidentId;
  const living = seats
    .filter((seat) => transaction.get(seat.resources.headquarters) > 0n)
    .sort((left, right) => left.seat.id.localeCompare(right.seat.id));
  const targetId =
    id === "meteor-warning" ? (living[random.bounded(living.length)] as SeatModel).seat.id : null;
  const effect =
    id === "meteor-warning"
      ? "200-power-generator-damage"
      : id === "valley-salvage-train"
        ? "100-metal-each"
        : "50-shield-charge-each";
  return Object.freeze({
    id,
    targetId,
    effect,
    announcedAtMs: transaction.gameTimeMs(),
    resolvesAtMs,
  });
}

function applyIncident(
  transaction: Transaction<Micro>,
  seats: readonly SeatModel[],
  lifecycle: MatchLifecycleState,
  incident: IncidentAnnouncement,
  shieldCapacities: ReadonlyMap<string, Micro>,
): boolean {
  const living = seats.filter((seat) => transaction.get(seat.resources.headquarters) > 0n);
  if (incident.id === "valley-salvage-train") {
    for (const seat of living) transaction.add(seat.resources.metal, microFromString("100"));
    return true;
  }
  if (incident.id === "clear-skies") {
    for (const seat of living) addShieldCharge(transaction, seat, lifecycle, shieldCapacities);
    return true;
  }
  const target = living.find((seat) => seat.seat.id === incident.targetId);
  if (!target) return false;
  const armor = microFromString(
    effectMultiplier(
      effectsForResearch(transaction.getRecord(target.research)),
      "non-hq-building-integrity",
    ),
  );
  const integrity = BigInt(buildingRule("power-generator").metalCost * 2) * armor;
  const destroyed = (microFromString("200") * 1_000_000n) / integrity;
  const current = transaction.get(target.resources.powerGenerator);
  transaction.set(
    target.resources.powerGenerator,
    (current > destroyed ? current - destroyed : 0n) as Micro,
  );
  return true;
}

function addShieldCharge(
  transaction: Transaction<Micro>,
  seat: SeatModel,
  lifecycle: MatchLifecycleState,
  capacities: ReadonlyMap<string, Micro>,
): void {
  const total = capacities.get(seat.seat.id) ?? 0n;
  const combat = transaction.getRecord(seat.combat);
  const borders = Object.fromEntries(
    seat.borderIds.map((id) => {
      const border = combat.borders[id] as (typeof combat.borders)[string];
      if (!borderIsActive(lifecycle, seat.seat.id, id)) return [id, border];
      const percent = transaction.getAllocation(seat.shieldAllocation.id, id) / 1_000_000n;
      const maximum = (total * percent) / 100n;
      const charge = microFromString(border.shieldCharge) + microFromString("50");
      return [
        id,
        { ...border, shieldCharge: formatMicro((charge < maximum ? charge : maximum) as Micro) },
      ];
    }),
  );
  transaction.setRecord(seat.combat, cloneCombatState({ borders }));
}

function pact(
  left: string,
  right: string,
  status: PactState["status"],
  proposals: PactState["proposals"],
  noticeEndsAtMs: number | null,
): PactState {
  return Object.freeze({
    left,
    right,
    status,
    proposals: Object.freeze(proposals),
    noticeEndsAtMs,
  });
}
